import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaSearch, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProjectCard from '../components/ProjectCard';
import SectionHeader from '../components/SectionHeader';
import projectsData from '../data/projects.json';
import projectTags from '../data/project-tags.json';
import '../styles/Projects.css';

const allTags = projectTags as string[];

const Projects: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isTagsExpanded, setIsTagsExpanded] = useState(false);

  // Number of tags to show when collapsed (approx 2 rows)
  const COLLAPSED_TAG_LIMIT = 10;

  // Filter projects based on search term and selected tags
  const filteredProjects = useMemo(() => {
    return projectsData.filter((project) => {
      const matchesSearch =
        searchTerm === '' ||
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));

      // Match ALL selected tags (AND logic)
      const matchesTags =
        selectedTags.length === 0 ||
        selectedTags.every((selectedTag) => project.tags.includes(selectedTag));

      return matchesSearch && matchesTags;
    });
  }, [searchTerm, selectedTags]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTags([]);
  };

  return (
    <div className="projects-page">
      <Navbar />

      <section className="projects-header-section">
        <div className="container">
          <SectionHeader
            title="My Projects"
            subtitle="A collection of my work that I am proud of showcasing various technologies and skills"
            centered
          />

          {/* Search and Filter */}
          <motion.div
            className="projects-filters"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search projects by name, description, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            <div className="tags-filter-container">
              <div className="tags-filter-header">
                <span className="tags-filter-label">
                  Filter by tags {selectedTags.length > 0 && `(${selectedTags.length} selected)`}
                </span>
                {selectedTags.length > 0 && (
                  <button
                    className="clear-tags-btn"
                    onClick={() => setSelectedTags([])}
                  >
                    Clear Tags
                  </button>
                )}
              </div>

              <div className={`tags-filter ${isTagsExpanded ? 'expanded' : 'collapsed'}`}>
                <button
                  className={`filter-tag all-tag ${selectedTags.length === 0 ? 'active' : ''}`}
                  onClick={() => setSelectedTags([])}
                >
                  All Projects
                </button>
                {(isTagsExpanded ? allTags : allTags.slice(0, COLLAPSED_TAG_LIMIT)).map((tag) => (
                  <button
                    key={tag}
                    className={`filter-tag ${selectedTags.includes(tag) ? 'active' : ''}`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                    {selectedTags.includes(tag) && <FaTimes className="tag-remove-icon" />}
                  </button>
                ))}
              </div>

              {allTags.length > COLLAPSED_TAG_LIMIT && (
                <button
                  className="expand-tags-btn"
                  onClick={() => setIsTagsExpanded(!isTagsExpanded)}
                >
                  {isTagsExpanded ? (
                    <>
                      <FaChevronUp /> Show Less
                    </>
                  ) : (
                    <>
                      <FaChevronDown /> Show {allTags.length - COLLAPSED_TAG_LIMIT} More Tags
                    </>
                  )}
                </button>
              )}
            </div>

            {(searchTerm || selectedTags.length > 0) && (
              <div className="active-filters">
                <span className="filters-label">Active filters:</span>
                {searchTerm && (
                  <span className="active-filter-tag">
                    Search: "{searchTerm}"
                  </span>
                )}
                {selectedTags.map((tag) => (
                  <span key={tag} className="active-filter-tag">
                    {tag}
                    <button
                      className="remove-filter-tag"
                      onClick={() => toggleTag(tag)}
                      aria-label={`Remove ${tag} filter`}
                    >
                      <FaTimes />
                    </button>
                  </span>
                ))}
                <button className="clear-all-filters" onClick={clearFilters}>
                  Clear All
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="projects-grid-section">
        <div className="container">
          {filteredProjects.length > 0 ? (
            <>
              <div className="results-count">
                Showing {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
              </div>
              <motion.div
                className="projects-grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {filteredProjects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                  >
                    <ProjectCard {...project} />
                  </motion.div>
                ))}
              </motion.div>
            </>
          ) : (
            <motion.div
              className="no-results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <h3>No projects found</h3>
              <p>Try adjusting your search or filter criteria</p>
              <button className="btn-primary" onClick={clearFilters}>
                Clear Filters
              </button>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Projects;

